import { IHCPFlowKanbanCard } from './hcp-flow-card.interface'
import { HCPFlowEnum, IHCPFlowKanbanColumn } from './hcp-flow.interface'
import {
  createMovementHistoryEntry,
  prependMovementHistory,
} from './hcp-flow-history.helpers'

export interface IFlowCardMovePayload {
  card: IHCPFlowKanbanCard
  sourceStatusId: string
  destStatusId: string
  destIndex: number
  destSequence: number
}

export function canDragHCPFlow(flow?: HCPFlowEnum): boolean {
  return flow === HCPFlowEnum.PRODUCTION || flow === HCPFlowEnum.LOGISTICS
}

export function getMoveTargetOrderProductIds(card: IHCPFlowKanbanCard): Array<string | number> {
  if (card.isLogisticsOrderCard && card.logisticsOrderItems?.length) {
    return card.logisticsOrderItems.map((item) => item.id)
  }

  return [card.id]
}

export function applyCardMove(
  columns: IHCPFlowKanbanColumn[],
  sourceStatusId: string,
  destStatusId: string,
  cardId: string | number,
  destIndex: number,
  destSequence: number,
  destColumnMeta: Pick<IHCPFlowKanbanColumn, 'name' | 'displayCode'>,
  movementActor?: string | null
): IHCPFlowKanbanColumn[] {
  const cardIdStr = String(cardId)
  const sourceColumn = columns.find((column) => column.statusId === sourceStatusId)
  const sourceCardIndex = sourceColumn?.cards.findIndex((card) => String(card.id) === cardIdStr) ?? -1
  const movedCard = sourceCardIndex >= 0 ? sourceColumn?.cards[sourceCardIndex] : undefined

  if (!movedCard || !sourceColumn) return columns

  const withoutSource = columns.map((column) => {
    if (column.statusId !== sourceStatusId) return column

    const newCards = [...column.cards]
    newCards.splice(sourceCardIndex, 1)

    return {
      ...column,
      cards: newCards,
      total: Math.max(0, column.total - 1),
    }
  })

  const movementHistory = movementActor
    ? createMovementHistoryEntry({
      flow: movedCard.hcpCurrentFlow,
      statusName: destColumnMeta.name,
      displayCode: destColumnMeta.displayCode,
      sequence: destSequence,
      actorUsername: movementActor,
    })
    : null

  const updatedCard: IHCPFlowKanbanCard = {
    ...movedCard,
    hcpCurrentFlowStatusSequence: destSequence,
    hcpCurrentFlowStatusName: destColumnMeta.name,
    hcpCurrentFlowDisplayCode: destColumnMeta.displayCode,
    displayCode: destColumnMeta.displayCode,
    updatedAt: new Date().toISOString(),
    hcpFlowHistories: movementHistory
      ? prependMovementHistory(movedCard.hcpFlowHistories, movementHistory)
      : movedCard.hcpFlowHistories,
    logisticsOrderItems: movedCard.logisticsOrderItems?.map((item) => ({
      ...item,
      hcpCurrentFlowStatusName: destColumnMeta.name,
      displayCode: destColumnMeta.displayCode,
    })),
  }

  return withoutSource.map((column) => {
    if (column.statusId !== destStatusId) return column

    const newCards = [...column.cards]
    newCards.splice(destIndex, 0, updatedCard)

    return {
      ...column,
      cards: newCards,
      total: column.total + 1,
    }
  })
}

export function patchMovedCardAfterApi(
  columns: IHCPFlowKanbanColumn[],
  cardId: string | number,
  destStatusId: string,
  updatedCards: IHCPFlowKanbanCard[],
  fallbackActor?: string | null
): IHCPFlowKanbanColumn[] {
  if (!updatedCards.length) return columns

  const cardIdStr = String(cardId)
  const apiCard = updatedCards.find((item) => String(item.id) === cardIdStr) ?? updatedCards[0]
  const histories = apiCard.hcpFlowHistories?.length
    ? apiCard.hcpFlowHistories
    : (
      fallbackActor
        ? prependMovementHistory(
          undefined,
          createMovementHistoryEntry({
            flow: apiCard.hcpCurrentFlow,
            statusName: apiCard.hcpCurrentFlowStatusName ?? '',
            displayCode: apiCard.hcpCurrentFlowDisplayCode ?? apiCard.displayCode,
            sequence: apiCard.hcpCurrentFlowStatusSequence,
            actorUsername: fallbackActor,
          })
        )
        : undefined
    )

  return columns.map((column) => {
    if (column.statusId !== destStatusId) return column

    return {
      ...column,
      cards: column.cards.map((card) => {
        if (String(card.id) !== cardIdStr) return card

        return {
          ...card,
          ...apiCard,
          hcpFlowHistories: histories ?? card.hcpFlowHistories,
        }
      }),
    }
  })
}
